import React, { useState } from "react";
import { BookOpenIcon, SparklesIcon, PlusIcon, TrashIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { useGeminiMutations } from "../../../../hooks/data/useProjectMutations";
import type { StoriesPhaseProps } from "./storiesPhase.types";
import type { ProjectStories, UserStory, Epic, AcceptanceCriteria } from "@/features/Projects";
import { StoriesSkeleton, StoryEditForm, StoriesSummary, StoryCard, EmptyStoriesState } from "./components";

import { getDefaultStories } from "./storiesPhase.definitions";
import { createNewStory, addStoryToCollection, updateStoryInCollection, removeStoryFromCollection } from "./utils/storyHelpers";

// StoryEditForm component has been extracted to ./components/StoryEditForm/

export default function StoriesPhase({ 
  project, 
  onSave, 
  onNext 
}: StoriesPhaseProps) {
  const [stories, setStories] = useState<ProjectStories>(
    project.stories || getDefaultStories()
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editingStory, setEditingStory] = useState<string | null>(null);
  
  
  const { generateUserStories, isGeneratingUserStories } = useGeminiMutations();

  // Handle generating stories with AI
  const handleGenerateFromAI = async () => {
    try {
      const response = await generateUserStories({
        projectId: project.id,
        projectDescription: project.description,
        objectives: project.objectives || [],
        technicalSolution: project.technicalSolution,
        timeline: project.timeline,
        includeEpics: true,
        focusAreas: []
      });
      
      const newStories = response.stories;
      setStories(newStories);
      
      // Auto-save the generated stories
      await onSave(newStories);
    } catch (error) {
      console.error("❌ Erro ao gerar user stories:", error);
    }
  };

  // Handle saving stories
  const handleSaveStories = async (updatedStories: ProjectStories) => {
    setStories(updatedStories);
    await onSave(updatedStories);
  };

  // Handle creating stories manually
  const handleCreateManually = () => {
    const basicStories: ProjectStories = {
      epics: [],
      stories: [],
      totalStoryPoints: 0,
      totalEstimatedHours: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setStories(basicStories);
    setIsEditing(true);
  };

  // Handle adding a new story
  const handleAddStory = () => {
    const newStory = createNewStory();
    const updatedStories = addStoryToCollection(stories, newStory);
    setStories(updatedStories);
    handleSaveStories(updatedStories);
  };

  // Handle deleting a story
  const handleDeleteStory = (storyId: string) => {
    const updatedStories = removeStoryFromCollection(stories, storyId);
    setStories(updatedStories);
    handleSaveStories(updatedStories);
  };

  // Handle editing a story
  const handleEditStory = (storyId: string) => {
    setEditingStory(storyId);
  };

  // Handle updating a story
  const handleUpdateStory = (updatedStory: UserStory) => {
    const updatedStories = updateStoryInCollection(stories, updatedStory);
    setStories(updatedStories);
    setEditingStory(null);
    handleSaveStories(updatedStories);
  };

  // Handle canceling story edit
  const handleCancelEditStory = () => {
    setEditingStory(null);
  };

  // Handle continuing to next phase
  const handleNext = async () => {
    if (stories.stories.length > 0) {
      await onSave(stories);
    }
    onNext();
  };

  const hasStories = stories.stories.length > 0;
  const hasStoriesOrEditing = hasStories || isEditing;
  const totalStoryPoints = stories.totalStoryPoints;
  const totalStories = stories.stories.length;
  const totalEpics = stories.epics.length;

  if (isGeneratingUserStories) {
    return <StoriesSkeleton />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BookOpenIcon className="h-5 w-5 text-indigo-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {hasStories ? "User Stories do Projeto" : "Criar User Stories"}
              </h2>
              <p className="text-sm text-gray-600">
                {hasStories 
                  ? `${totalStories} stories • ${totalStoryPoints} story points • ${totalEpics} épicos`
                  : "Gere automaticamente as histórias de usuário do projeto"
                }
              </p>
            </div>
          </div>
          
          {hasStories && (
            <Button
              onClick={() => setIsEditing(!isEditing)}
              variant="secondary"
              size="sm"
            >
              {isEditing ? "Visualizar" : "Editar Stories"}
            </Button>
          )}
        </div>

        {/* Stories Summary when collapsed */}
        {hasStories && !isEditing && (
          <StoriesSummary
            totalStories={totalStories}
            totalStoryPoints={totalStoryPoints}
            totalEpics={totalEpics}
          />
        )}
      </div>

      {/* Generate AI Stories Option */}
      {!hasStories && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 p-2 bg-indigo-100 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-md font-medium text-gray-900 mb-1">
                Gerar User Stories com IA
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Com base na descrição do projeto, objetivos, solução técnica e timeline, 
                a IA criará automaticamente user stories detalhadas com critérios de aceitação.
              </p>
              <div className="flex space-x-2">
                <Button
                  onClick={handleGenerateFromAI}
                  disabled={isGeneratingUserStories}
                  leftIcon={<SparklesIcon className="h-4 w-4" />}
                  size="sm"
                >
                  {isGeneratingUserStories ? "Gerando..." : "Gerar Stories"}
                </Button>
                <Button
                  onClick={handleCreateManually}
                  variant="secondary"
                  size="sm"
                >
                  Criar Manualmente
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stories Visualization */}
      {hasStoriesOrEditing && isEditing && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-md font-medium text-gray-900">
              Editar User Stories
            </h3>
            <div className="flex space-x-2">
              <Button
                onClick={handleGenerateFromAI}
                disabled={isGeneratingUserStories}
                variant="secondary"
                size="sm"
                leftIcon={<SparklesIcon className="h-4 w-4" />}
              >
                Regerar com IA
              </Button>
              <Button
                onClick={handleAddStory}
                variant="secondary"
                size="sm"
                leftIcon={<PlusIcon className="h-4 w-4" />}
              >
                Adicionar Story
              </Button>
            </div>
          </div>
          
          {/* Stories List */}
          <div className="space-y-3">
            {stories.stories.length === 0 ? (
              <EmptyStoriesState onAddStory={handleAddStory} />
            ) : (
              stories.stories.map((story, index) => (
                <StoryCard
                  key={story.id || `story-${index}-${Date.now()}`}
                  story={story}
                  index={index}
                  epics={stories.epics}
                  isEditing={editingStory === story.id}
                  onEdit={handleEditStory}
                  onDelete={handleDeleteStory}
                  onSave={handleUpdateStory}
                  onCancel={handleCancelEditStory}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sticky bottom-4 shadow-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {hasStories 
              ? "User stories criadas • Alterações salvas automaticamente"
              : "Crie user stories para continuar"
            }
          </span>
          
          <Button
            onClick={handleNext}
            disabled={!hasStories}
            className="px-6"
          >
            Continuar para Schema Técnico
          </Button>
        </div>
      </div>
    </div>
  );
}